/**
 * Institutional referral campaign progress, shown inside Share & Earn. DARK:
 * ShareEarnModal mounts this only when REACT_APP_INSTITUTIONAL_PROMOTION_ENABLED
 * is "true"; the server answers 404 while its promotion flag is off.
 *
 * Everything shown is counts and amounts for the signed-in referrer — the API
 * never returns referee emails or scores, and this UI never asks for them.
 * Qualification is decided by the server (server time, unique answered
 * questions); this panel only explains the published rules transparently.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { getReferralProgress, INSTITUTIONAL_CODES } from "./institutional-api";
import { formatCents } from "./institutional-copy";
import "./institutional.scss";

// Campaign milestones (CONTRACT.txt business rules): $50 at 5 qualified
// referrals, another $100 at 10 — $150 total. The API reports progress and the
// NEXT milestone, not the schedule, so the schedule copy lives here.
export const CAMPAIGN_MILESTONES = [
  { count: 5, cents: 5000 },
  { count: 10, cents: 10000 },
];
export const CAMPAIGN_TOTAL_CENTS = CAMPAIGN_MILESTONES.reduce((sum, m) => sum + m.cents, 0);
const FINAL_MILESTONE_COUNT = CAMPAIGN_MILESTONES[CAMPAIGN_MILESTONES.length - 1].count;

// Fallbacks mirror the contract defaults; the server's `requirements` object
// wins whenever it is present so the copy always matches what is enforced.
export const DEFAULT_REQUIREMENTS = {
  minimumQuestions: 10,
  quizCount: 7,
  practiceDays: 7,
  minimumDays: 7,
  scoreGreaterThan: 50,
};

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
};

const positiveOr = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

export const resolveRequirements = (requirements) => {
  const r = requirements && typeof requirements === "object" ? requirements : {};
  return {
    minimumQuestions: positiveOr(r.minimumQuestions, DEFAULT_REQUIREMENTS.minimumQuestions),
    quizCount: positiveOr(r.quizCount, DEFAULT_REQUIREMENTS.quizCount),
    practiceDays: positiveOr(r.practiceDays, DEFAULT_REQUIREMENTS.practiceDays),
    minimumDays: positiveOr(r.minimumDays, DEFAULT_REQUIREMENTS.minimumDays),
    // 0 is a meaningful threshold ("above 0%"), so only reject non-numbers.
    scoreGreaterThan: Number.isFinite(Number(r.scoreGreaterThan)) && r.scoreGreaterThan !== null
      ? Number(r.scoreGreaterThan)
      : DEFAULT_REQUIREMENTS.scoreGreaterThan,
  };
};

const plural = (n, one, many) => (n === 1 ? one : many);

const isHttpUrl = (value) => typeof value === "string" && /^https?:\/\/\S+$/i.test(value);

export default function InstitutionalReferralProgress({ onReferralLink }) {
  const [state, setState] = useState({ status: "loading" });
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  // Held in a ref so an inline callback from the host can't re-trigger the
  // fetch on every render.
  const onReferralLinkRef = useRef(onReferralLink);
  onReferralLinkRef.current = onReferralLink;

  const load = useCallback(async () => {
    setState({ status: "loading" });
    const result = await getReferralProgress();
    if (!mountedRef.current) return;
    if (result.ok && result.data && typeof result.data === "object") {
      setState({ status: "ready", data: result.data });
      if (onReferralLinkRef.current && isHttpUrl(result.data.referralLink)) {
        onReferralLinkRef.current(result.data.referralLink);
      }
    } else {
      setState({ status: "error", code: result.ok ? INSTITUTIONAL_CODES.BAD_RESPONSE : result.code });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <Box className="institutional-panel" data-testid="institutional-referral-loading">
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (state.status === "error") {
    const retryable = state.code !== INSTITUTIONAL_CODES.NOT_FOUND
      && state.code !== INSTITUTIONAL_CODES.FORBIDDEN;
    return (
      <Box className="institutional-panel" data-testid="institutional-referral-error">
        <Typography variant="body2">
          Institutional referral progress isn&apos;t available right now.
        </Typography>
        {retryable && (
          <Box className="institutional-actions">
            <Button size="small" color="primary" onClick={load} data-testid="institutional-referral-retry">
              Try again
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  const { data } = state;
  const qualified = toCount(data.qualifiedCount);
  const pending = toCount(data.pendingCount);
  const earnedCents = toCount(data.earnedCents);
  const fulfilledCents = Math.min(toCount(data.fulfilledCents), earnedCents);
  const awaitingCents = earnedCents - fulfilledCents;
  const req = resolveRequirements(data.requirements);
  const hoursAfterSignup = Math.round(req.minimumDays * 24);

  // nextMilestoneCount is the qualified-referral threshold of the next reward
  // (5 or 10); null/absent once every milestone is reached.
  const hasNext = data.nextMilestoneCount !== null && data.nextMilestoneCount !== undefined
    && Number.isFinite(Number(data.nextMilestoneCount));
  const nextCount = hasNext ? toCount(data.nextMilestoneCount) : null;
  const remaining = hasNext ? Math.max(0, nextCount - qualified) : 0;
  const nextCents = data.nextMilestoneCents !== null && data.nextMilestoneCents !== undefined
    && Number.isFinite(Number(data.nextMilestoneCents))
    ? Number(data.nextMilestoneCents)
    : CAMPAIGN_MILESTONES.find((m) => m.count === nextCount)?.cents;
  const progressPercent = Math.min(100, Math.round((Math.min(qualified, FINAL_MILESTONE_COUNT) / FINAL_MILESTONE_COUNT) * 100));

  return (
    <Box className="institutional-panel" data-testid="institutional-referral-progress">
      <Typography variant="subtitle1" component="h3">
        Institutional referral reward
      </Typography>
      <Typography variant="body2" data-testid="institutional-referral-schedule">
        Earn up to <strong>{formatCents(CAMPAIGN_TOTAL_CENTS)}</strong>:{" "}
        <strong>{formatCents(CAMPAIGN_MILESTONES[0].cents)}</strong> when {CAMPAIGN_MILESTONES[0].count} friends you
        refer qualify, plus another <strong>{formatCents(CAMPAIGN_MILESTONES[1].cents)}</strong> when{" "}
        {CAMPAIGN_MILESTONES[1].count} qualify.
      </Typography>

      <Box
        className="institutional-progress-bar"
        role="progressbar"
        aria-label="Qualified referrals"
        aria-valuemin={0}
        aria-valuemax={FINAL_MILESTONE_COUNT}
        aria-valuenow={Math.min(qualified, FINAL_MILESTONE_COUNT)}
      >
        <Box className="institutional-progress-fill" style={{ width: `${progressPercent}%` }} />
      </Box>
      <Box className="institutional-milestones" aria-hidden="true">
        <span>0</span>
        <span>{CAMPAIGN_MILESTONES[0].count} · {formatCents(CAMPAIGN_MILESTONES[0].cents)}</span>
        <span>{CAMPAIGN_MILESTONES[1].count} · +{formatCents(CAMPAIGN_MILESTONES[1].cents)}</span>
      </Box>

      <Typography variant="body2" data-testid="institutional-referral-counts">
        <strong>{qualified}</strong> qualified · <strong>{pending}</strong> pending
      </Typography>
      <Typography variant="caption" className="institutional-otp-timer">
        Pending friends signed up with your link and are still working toward the requirements below. Only
        qualified friends count toward rewards.
      </Typography>

      <Typography variant="body2" data-testid="institutional-referral-next">
        {hasNext
          ? `${remaining} more qualified ${plural(remaining, "friend", "friends")} to earn ${formatCents(nextCents)}.`
          : qualified >= FINAL_MILESTONE_COUNT
            ? `You've reached the ${formatCents(CAMPAIGN_TOTAL_CENTS)} campaign maximum. Thank you!`
            : "No further rewards are available in this campaign."}
      </Typography>

      <Typography variant="body2" data-testid="institutional-referral-earned">
        Earned {formatCents(earnedCents)} · Sent {formatCents(fulfilledCents)}
      </Typography>
      {awaitingCents > 0 && (
        <Typography variant="caption" data-testid="institutional-referral-awaiting">
          {formatCents(awaitingCents)} is being processed. Rewards are sent manually by our team as an Amazon gift
          card or cash.
        </Typography>
      )}

      <Typography variant="body2" component="h4" style={{ marginTop: 8 }}>
        <strong>How a friend qualifies</strong>
      </Typography>
      <Box component="ol" className="institutional-requirements" data-testid="institutional-referral-requirements">
        <li>They sign up through your link with a verified institutional (.edu or .org) email.</li>
        <li>
          At least {req.minimumDays} {plural(req.minimumDays, "day", "days")} ({hoursAfterSignup} hours) pass after
          their verified signup.
        </li>
        <li>
          They complete {req.quizCount} full {plural(req.quizCount, "quiz", "quizzes")} of at least{" "}
          {req.minimumQuestions} questions each, answering every question and scoring above {req.scoreGreaterThan}% on
          each quiz.
        </li>
        <li>
          Those quizzes are on {req.practiceDays} different {plural(req.practiceDays, "day", "days")} (UTC calendar
          days).
        </li>
        {/* Mirrors InstitutionalReferralCampaignService.recordQuizCompletion's exclusions. */}
        <li data-testid="institutional-referral-excluded-quizzes">
          Each quiz is submitted by your friend from their own account. Quizzes that are ended early, taken in tutor
          mode, completed together with a tutor, or built from a tutoring session don&apos;t count.
        </li>
      </Box>
      <Typography variant="caption" className="institutional-otp-timer">
        A phone number isn&apos;t required. Progress is checked by our servers and may not update immediately. Adding an
        institutional email to an account that already existed doesn&apos;t count as a new referral. For privacy, we
        only show counts — never your friends&apos; emails or scores.
      </Typography>
    </Box>
  );
}
